"use client"

/**
 * What the Pricing v5 drafts share: prices, the check-up being put
 * together, the booking link, the bar that holds the selection, and the row
 * of links between the drafts. The picks and popups in the booking's style
 * are in pricing-v5-booking.tsx.
 *
 * The selection (Eugene, 30.09):
 * - It starts empty. The plan is in every check-up, so it is not a pick,
 *   and a bar that opens with the plan already in reads as if something had
 *   been chosen for you. The bar comes up with the first thing added, and
 *   the plan's line says it is in every check-up instead of offering a
 *   remove button.
 * - Every added line can be removed, "Clear all" empties the selection,
 *   and "Undo" brings it back for a few seconds.
 * - An imaging test is listed under the MRI it goes with and leaves with it.
 *
 * The booking link carries the whole selection: the scan's package and the
 * single tests, by their booking ids (ADD_ON_PRODUCT_IDS), in the site's
 * `?products=` deep link (lib/booking.ts). The membership-first app attaches
 * the scan to the plan and picks the tests it can carry (ahead-booking-new
 * `apps/membership-first/src/entryBasket.ts`).
 */

import {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react"
import { useLocale } from "next-intl"
import { useCountry } from "@/components/layout/country-provider"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import {
  CheckCircleNeutralIcon,
  CheckmarkIcon,
  ChevronDownIcon,
  CloseIcon,
} from "@/components/ui/icons"
import type { BiomarkerGroup } from "@/components/ui/product-info-modal"
import {
  ADD_ON_PRODUCT_IDS,
  BOOKING_PACKAGE_TYPE,
  bookingUrlForCountry,
} from "@/lib/booking"
import { track } from "@/lib/analytics"
import { groupItemsByCmsCategory } from "@/lib/categories"
import { formatCurrency } from "@/lib/format"
import {
  buildFallback,
  fetchPrices,
  membershipTierAmount,
  MEMBERSHIP_PRICE_PENDING,
  type MembershipTierId,
  type PriceData,
} from "@/lib/pricing"
import { memberPriceChf, scanOnTopOfPlan } from "@/lib/pricing/member-prices"
import { resolveServiceAvailability } from "@/lib/service-availability"
import { isBookingOpen } from "@/i18n/config"
import { cn } from "@/lib/utils"
import {
  cartSnapshot as savedCartSnapshot,
  serverCart,
  setCart,
  subscribeCart,
} from "@/lib/saved-check-up"
import { bookingFlowUrl, type BookingScan } from "../home/home-v4-data"
import {
  PLAN_IMAGING_ADDONS,
  PLAN_TIERS,
  type MRI_PACKAGES,
} from "./pricing-v4-data"
import { LPA_GIFT_KEY, useLpaGift } from "../lpa-gift"
import type { usePricingV5Copy } from "./pricing-v5-copy"
import type { PricingV5Data } from "./pricing-v5-page-data"

/** home-v4's content column (`SECTION` in home-v4-client.tsx). */
export const SECTION = "mx-auto max-w-[1440px] px-4 md:px-10"

export type Copy = ReturnType<typeof usePricingV5Copy>
export type MriId = (typeof MRI_PACKAGES)[number]["id"]

/** The single tests that go with an MRI in the same appointment. */
export const IMAGING_KEYS: ReadonlySet<string> = new Set(
  PLAN_IMAGING_ADDONS.map((a) => a.titleKey)
)

/** How long "Undo" stays after "Clear all". */
const UNDO_MS = 8000

/* ── Prices and what the CMS says ──────────────────────────────────────── */

/**
 * The price feed the server read for the page (`PricingV5Data`), or on a
 * draft that loads none, the browser's own fetch, as `useLivePrices` does.
 */
function usePriceFeed(country: string, initial?: PriceData | null) {
  const [feed, setFeed] = useState<PriceData>(
    () => initial ?? buildFallback(country)
  )
  useEffect(() => {
    if (initial) {
      setFeed(initial)
      return
    }
    let live = true
    fetchPrices(country).then((prices) => {
      if (live) setFeed(prices)
    })
    return () => {
      live = false
    }
  }, [country, initial])
  return feed
}

/**
 * Same as v4: list prices from the live feed, plan prices from its member
 * prices. Anything missing shows a dash rather than a wrong figure. Also the
 * CMS's word on each item: whether the market can book it, and the
 * biomarkers an info pop-up lists.
 */
function useLivePlanPrices(data?: PricingV5Data) {
  const { code: country } = useCountry()
  const live = usePriceFeed(country, data?.initialPrices)
  return useMemo(() => {
    // The plan's yearly price per rhythm, from the feed's membership tiers
    // (the market's own defaults when it has none, null where the market has
    // no membership price), and the once-a-year one every check-up starts
    // from.
    const tier = (id: MembershipTierId) =>
      membershipTierAmount(live.prices, id, country)
    const plan = tier(PLAN_TIERS[0].id)
    // A market without a plan price has no total either: every figure built
    // on the plan says what it waits for.
    const money = (amount: number | null) =>
      amount == null || !Number.isFinite(amount)
        ? plan == null
          ? MEMBERSHIP_PRICE_PENDING
          : "—"
        : formatCurrency(amount, country)
    const list = (key: string) =>
      live.prices[key] ? Number(live.prices[key]) : null
    const member = (key: string) => memberPriceChf(live.prices, key)
    // A scan on top of the plan: member price minus the plan's own blood
    // test (see MRI_PACKAGES in pricing-v4-data.ts). The quiz result prices
    // its scan with the same helper.
    const onTop = (key: string) => scanOnTopOfPlan(live.prices, key, plan)
    // Today's /pricing does the same grouping (pricing-client.tsx).
    const biomarkersOf = (packageId?: string): BiomarkerGroup[] | undefined => {
      if (!packageId || !data?.cmsBiomarkers.length) return undefined
      const groups = groupItemsByCmsCategory(
        data.cmsBiomarkers.filter((b) => b.packages.includes(packageId)),
        data.cmsCategories
      ).map(({ category, items }) => ({
        category: category.name,
        items: items.map((b) => b.name),
      }))
      return groups.length > 0 ? groups : undefined
    }
    // Scans and imaging tests are MRI products, so a market whose MRI is
    // not open yet shows them that way, as ImagingAddonsSection does.
    const availability = (slug: string | undefined) =>
      slug
        ? resolveServiceAvailability({
            country,
            category: "mri",
            pathOrSlug: slug,
            servicesConfig: data?.servicesConfig ?? [],
          })
        : ("bookable" as const)
    return {
      money,
      list,
      member,
      tier,
      plan,
      onTop,
      biomarkersOf,
      availability,
    }
  }, [live, country, data])
}

const PlanPricesContext = createContext<ReturnType<
  typeof useLivePlanPrices
> | null>(null)

/**
 * One price feed per draft. The selection mounts a chip or a line per pick,
 * so the page reads the feed once and everything below shares it.
 */
export function PlanPricesProvider({
  data,
  children,
}: {
  data?: PricingV5Data
  children: ReactNode
}) {
  const prices = useLivePlanPrices(data)
  return (
    <PlanPricesContext.Provider value={prices}>
      {children}
    </PlanPricesContext.Provider>
  )
}

export function usePlanPrices() {
  const prices = useContext(PlanPricesContext)
  if (!prices) throw new Error("usePlanPrices needs a PlanPricesProvider")
  return prices
}

/* ── The check-up being put together ───────────────────────────────────── */

type Picks = { mri: MriId | null; tests: string[] }

// The store itself is in lib/saved-check-up.ts, where every Book button
// reads it.
let undoTimer: ReturnType<typeof setTimeout> | null = null

/** The saved picks, with the MRI as the builder's package id. */
function cartSnapshot() {
  return savedCartSnapshot() as Picks & { cleared: Picks | null }
}

export function useCheckUp() {
  const { mri, tests, cleared } = useSyncExternalStore(
    subscribeCart,
    cartSnapshot,
    serverCart as typeof cartSnapshot
  )
  // The imaging test someone tried to add without an MRI.
  const [hint, setHint] = useState<string | null>(null)

  // Any change ends the undo: bringing back the old picks would drop the
  // new ones.
  const write = (picks: Picks, keepUndo = false) => {
    if (!keepUndo && undoTimer) clearTimeout(undoTimer)
    setCart({ ...picks, cleared: keepUndo ? cartSnapshot().cleared : null })
  }
  const change = () => setHint(null)

  // Imaging tests leave with the scan they go with.
  const withoutImaging = (all: string[]) =>
    all.filter((key) => !IMAGING_KEYS.has(key))

  /** The check-up as tracked: the scan's package first, then the tests. */
  const contents = (picks: Picks) =>
    picks.mri ? [picks.mri, ...picks.tests] : [...picks.tests]

  /**
   * `auto`: the scan an imaging test's Save brings along when none is saved
   * yet (CatalogueSave, HeroSaveButton), tracked apart from a picked scan.
   */
  const toggleMri = (id: MriId, { auto = false } = {}) => {
    change()
    const now = cartSnapshot()
    const next = now.mri === id ? null : id
    const picks = {
      mri: next,
      tests: next ? now.tests : withoutImaging(now.tests),
    }
    write(picks)
    if (next) track.checkUpItemSaved("mri", id, contents(picks), auto)
    else track.checkUpItemRemoved("mri", id, "save_button", contents(picks))
  }

  const toggleTest = (key: string) => {
    const now = cartSnapshot()
    if (IMAGING_KEYS.has(key) && !now.mri) {
      setHint(key)
      track.checkUpSaveBlocked(key)
      return
    }
    change()
    const saved = !now.tests.includes(key)
    const picks = {
      mri: now.mri,
      tests: saved ? [...now.tests, key] : now.tests.filter((k) => k !== key),
    }
    write(picks)
    if (saved) track.checkUpItemSaved("test", key, contents(picks))
    else track.checkUpItemRemoved("test", key, "save_button", contents(picks))
  }

  const removeMri = () => {
    change()
    const now = cartSnapshot()
    const picks = { mri: null, tests: withoutImaging(now.tests) }
    write(picks)
    if (now.mri)
      track.checkUpItemRemoved("mri", now.mri, "cart", contents(picks))
  }

  const removeTest = (key: string) => {
    change()
    const now = cartSnapshot()
    const picks = { mri: now.mri, tests: now.tests.filter((k) => k !== key) }
    write(picks)
    track.checkUpItemRemoved("test", key, "cart", contents(picks))
  }

  const clear = () => {
    setHint(null)
    const now = cartSnapshot()
    if (undoTimer) clearTimeout(undoTimer)
    track.checkUpCleared(contents(now))
    setCart({
      mri: null,
      tests: [],
      cleared: { mri: now.mri, tests: now.tests },
    })
    undoTimer = setTimeout(() => {
      undoTimer = null
      setCart({ ...cartSnapshot(), cleared: null })
    }, UNDO_MS)
  }

  const undo = () => {
    const back = cartSnapshot().cleared
    if (!back) return
    write(back)
    track.checkUpClearUndone(contents(back))
  }

  return {
    mri,
    tests,
    hint,
    count: (mri ? 1 : 0) + tests.length,
    canUndo: cleared != null,
    toggleMri,
    toggleTest,
    removeMri,
    removeTest,
    clear,
    undo,
  }
}

export type CheckUp = ReturnType<typeof useCheckUp>

export type Line = {
  key: string
  label: string
  amount: number | null
  /** An imaging test, listed under its MRI. */
  nested?: boolean
  remove: () => void
}

/**
 * The check-up as lines: the plan, the MRI with its imaging tests under
 * it, then the blood tests in page order. The MRI counts at its price on
 * top of the plan, so the lines add up to the total.
 */
export function useSummary(
  checkUp: CheckUp,
  { MRI, ADDONS }: Pick<Copy, "MRI" | "ADDONS">
) {
  const { member, onTop, plan } = usePlanPrices()
  const { code: country } = useCountry()
  // The membership includes Lp(a) in Switzerland (AHD-14738): a test saved
  // before that is neither charged here nor sent to the booking twice.
  const gift = useLpaGift()
  // The cart is one saved check-up for every market: an MRI picked in CH
  // stays out of the summary and the booking link where the MRI does not
  // book (NL, AHD-14582).
  const pkg = isBookingOpen(country, "mri")
    ? MRI.packages.find((p) => p.id === checkUp.mri)
    : undefined
  const picked = ADDONS.groups
    .flatMap((group): { key: string; name: string }[] => group.items)
    .filter((item) => checkUp.tests.includes(item.key))
    .filter((item) => !(gift && item.key === LPA_GIFT_KEY))
  const testLine = (item: { key: string; name: string }): Line => ({
    key: item.key,
    label: item.name,
    amount: member(item.key),
    nested: IMAGING_KEYS.has(item.key),
    remove: () => checkUp.removeTest(item.key),
  })
  const added: Line[] = [
    ...(pkg
      ? [
          {
            key: pkg.id,
            label: pkg.name,
            amount: onTop(pkg.priceKey),
            remove: checkUp.removeMri,
          },
        ]
      : []),
    ...picked.filter((item) => IMAGING_KEYS.has(item.key)).map(testLine),
    ...picked.filter((item) => !IMAGING_KEYS.has(item.key)).map(testLine),
  ]
  const total =
    plan == null || added.some((line) => line.amount == null)
      ? null
      : added.reduce<number>((sum, line) => sum + (line.amount ?? 0), plan)
  const scan: BookingScan = pkg ? pkg.scan : "blood"
  // The tests' booking ids, for the link. One the booking does not sell yet
  // has none and stays on the site.
  const addOnIds = picked
    .map((item) => ADD_ON_PRODUCT_IDS[item.key])
    .filter((id): id is string => !!id)
  return { plan, added, total, scan, addOnIds }
}

export type Summary = ReturnType<typeof useSummary>

/**
 * The booking link for the check-up on the page: the plan-first flow with
 * the scan and the tests, tagged as coming from here. On the
 * membership-first site (RELEASE_MEMBERSHIP_FIRST) `bookingFlowUrl` gives
 * nothing and BookingCta links to that site's booking app with the same
 * `products`.
 */
export function bookingHref(
  scan: BookingScan,
  addOnIds: readonly string[] = []
) {
  const flowUrl: string | undefined = bookingFlowUrl(scan, addOnIds)
  if (!flowUrl) return undefined
  const url = new URL(flowUrl)
  url.searchParams.set("from", "pricing")
  return url.toString()
}

export function productIdFor(scan: BookingScan) {
  if (scan === "pro") return PRODUCT_IDS.pro
  if (scan === "core") return PRODUCT_IDS.core
  if (scan === "blood") return PRODUCT_IDS.bloodTest
  return PRODUCT_IDS.advanced
}

/** True while any part of the element is on screen. */
export function useOnScreen<T extends Element>() {
  const ref = useRef<T>(null)
  const [onScreen, setOnScreen] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) =>
      setOnScreen(entry.isIntersecting)
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return [ref, onScreen] as const
}

/* ── Small parts ───────────────────────────────────────────────────────── */

function Tick() {
  return (
    <span className="text-deep-green-500 mt-0.5 inline-flex shrink-0">
      <CheckCircleNeutralIcon size={20} />
    </span>
  )
}

export function Included({
  items,
  size = "lg",
  strongFirst = false,
}: {
  items: string[]
  size?: "md" | "lg"
  /** The first line in medium weight, for a list that opens on a lead. */
  strongFirst?: boolean
}) {
  return (
    <ul className={cn("flex flex-col", size === "lg" ? "gap-4" : "gap-3")}>
      {items.map((item, i) => {
        const strong = strongFirst && i === 0
        return (
          <li key={item} className="flex gap-3">
            <Tick />
            <span
              className={cn(
                strong ? "text-neutral-900" : "text-neutral-800",
                size === "lg"
                  ? strong
                    ? "type-body-lg-emphasized"
                    : "type-body-lg"
                  : strong
                    ? "type-body-md-emphasized"
                    : "type-body-md"
              )}
            >
              {item}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/** The booking button, with the check-up's scan and tests in the link. */
export function BookButton({
  scan,
  addOnIds = [],
  label,
  trackingLabel,
  trackingLocation,
  size,
  arrow = true,
  className,
  buttonClassName,
  children,
}: {
  scan: BookingScan
  /** The saved tests' booking ids (useSummary), after the scan's package. */
  addOnIds?: readonly string[]
  label?: string
  trackingLabel: string
  trackingLocation: string
  /** `lg` is the DS 52px button, for the plan card. */
  size?: "default" | "lg"
  /** The plan card's button has no arrow (Eugene's Figma). */
  arrow?: boolean
  className?: string
  buttonClassName?: string
  children?: ReactNode
}) {
  return (
    <BookingCta
      productId={[productIdFor(scan), ...addOnIds]}
      // A scan's CTA opens the app on the MRI tab: without it BookingCta takes
      // the blood default and sends `packageType=blood-only`, and the app
      // drops the scan from the basket (Eugene, 08.10).
      category={scan === "blood" ? "blood" : "mri"}
      step="product"
      overrideHref={bookingHref(scan, addOnIds)}
      trackingLabel={trackingLabel}
      trackingLocation={trackingLocation}
      className={className}
    >
      <Button
        variant="primary"
        size={size}
        className={cn(
          "group bg-neutral-950 px-6 focus-visible:ring-neutral-400 md:px-8",
          buttonClassName
        )}
        iconRight={
          arrow ? (
            <ArrowRightIcon className="hidden transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1 md:block" />
          ) : undefined
        }
      >
        {children ?? label}
      </Button>
    </BookingCta>
  )
}

/**
 * "Book it without the plan", a one-time check-up in today's booking flow.
 * The link asks for the legacy flow because on the membership-first site
 * BookingCta sends everything else to the plan-first app, which sells no
 * check-up without the plan.
 */
export function OneTimeLink({
  scan = "blood",
  trackingLabel,
  trackingLocation,
  className = "text-neutral-900 underline underline-offset-4 hover:text-neutral-600",
  children,
}: {
  /** What today's flow opens on: the blood test, or an MRI package. */
  scan?: BookingScan
  trackingLabel: string
  trackingLocation: string
  className?: string
  children: ReactNode
}) {
  const locale = useLocale()
  const { code: country } = useCountry()
  const productId = productIdFor(scan)
  return (
    <BookingCta
      productId={productId}
      step="product"
      overrideHref={bookingUrlForCountry(
        country,
        locale,
        productId,
        "product",
        scan === "blood"
          ? BOOKING_PACKAGE_TYPE.blood
          : BOOKING_PACKAGE_TYPE.mri,
        "legacy"
      )}
      trackingLabel={trackingLabel}
      trackingLocation={trackingLocation}
      className={className}
    >
      {children}
    </BookingCta>
  )
}

/* ── The selection's controls ──────────────────────────────────────────── */

function RemoveButton({
  label,
  onClick,
  size = "md",
}: {
  label: string
  onClick: () => void
  size?: "sm" | "md"
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full text-neutral-700 transition-colors hover:bg-neutral-200 hover:text-neutral-900 focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:outline-none",
        size === "sm" ? "size-7" : "size-9"
      )}
    >
      {/* Bigger and heavier than the icon's own 2/24 line (Eugene, 30.09:
          "the cross thicker, bigger"). */}
      <CloseIcon
        width={size === "sm" ? 14 : 18}
        height={size === "sm" ? 14 : 18}
        className="[&_path]:[stroke-width:2.75]"
      />
    </button>
  )
}

export function ClearAllButton({
  onClick,
  SUMMARY,
  className,
}: {
  onClick: () => void
  SUMMARY: Copy["SUMMARY"]
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "type-body-sm text-neutral-600 underline underline-offset-4 hover:text-neutral-900",
        className
      )}
    >
      {SUMMARY.clearAll}
    </button>
  )
}

/** "Selection cleared. Undo", while the undo lasts. */
export function UndoNote({
  checkUp,
  SUMMARY,
  className,
}: {
  checkUp: CheckUp
  SUMMARY: Copy["SUMMARY"]
  className?: string
}) {
  return (
    <p role="status" className={cn("type-body-md text-neutral-700", className)}>
      {checkUp.canUndo && (
        <>
          {SUMMARY.cleared}{" "}
          <button
            type="button"
            onClick={checkUp.undo}
            className="font-medium text-neutral-900 underline underline-offset-4"
          >
            {SUMMARY.undo}
          </button>
        </>
      )}
    </p>
  )
}

/**
 * The check-up as a list: the plan first, marked as in every check-up
 * where the other lines have their remove button, then what was added.
 */
export function LineList({
  summary,
  SUMMARY,
  className,
}: {
  summary: Summary
  SUMMARY: Copy["SUMMARY"]
  className?: string
}) {
  const { money } = usePlanPrices()
  return (
    <ul className={cn("flex flex-col", className)}>
      <li className="flex items-start gap-3 py-2.5">
        <div className="min-w-0 flex-1">
          {/* Names medium and prices regular, one size, under the list's
              title (Eugene, 01.10: "a proper type hierarchy"). */}
          <p className="type-body-lg-emphasized text-neutral-900">
            {SUMMARY.planLine}
          </p>
          <p className="type-body-sm text-deep-green-600 mt-0.5 inline-flex items-center gap-1">
            <CheckmarkIcon width={14} height={14} />
            {SUMMARY.alwaysIn}
          </p>
        </div>
        <p className="type-body-lg whitespace-nowrap text-neutral-700 tabular-nums">
          {money(summary.plan)}
        </p>
        {/* Keeps the prices in one column with the lines that can go. */}
        <span aria-hidden className="size-8 shrink-0" />
      </li>
      {summary.added.map((line) => (
        <li
          key={line.key}
          className="flex items-center gap-3 border-t border-neutral-100 py-1.5"
        >
          <p
            className={cn(
              // Medium, the names read too thin (Eugene, 01.10).
              "type-body-lg-emphasized min-w-0 flex-1 text-neutral-900",
              line.nested && "border-l-2 border-neutral-200 pl-3"
            )}
          >
            {line.label}
          </p>
          <p className="type-body-lg whitespace-nowrap text-neutral-700 tabular-nums">
            {money(line.amount)}
          </p>
          <RemoveButton
            label={SUMMARY.remove(line.label)}
            onClick={line.remove}
          />
        </li>
      ))}
    </ul>
  )
}

/**
 * One added line as a chip, for the bar's single row from md up. No price:
 * the total beside the chips carries the money (Eugene, 30.09).
 */
function LineChip({ line, SUMMARY }: { line: Line; SUMMARY: Copy["SUMMARY"] }) {
  return (
    <span className="type-body-md inline-flex h-9 max-w-full items-center gap-1 rounded-full bg-neutral-100 pr-1 pl-3.5 text-neutral-900">
      <span className="max-w-[220px] truncate" title={line.label}>
        {line.label}
      </span>
      <RemoveButton
        size="sm"
        label={SUMMARY.remove(line.label)}
        onClick={line.remove}
      />
    </span>
  )
}

/** The plan's chip: first, a little stronger, and with no remove button. */
function PlanChip({ label }: { label: string }) {
  return (
    <span className="type-title-sm bg-deep-green-100 text-deep-green-700 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3.5 whitespace-nowrap">
      <CheckmarkIcon width={16} height={16} />
      {label}
    </span>
  )
}

/**
 * True while `content`, laid out at its own width, fits in `box`. Both are
 * watched, so adding a line, a font arriving or a narrower window all
 * count.
 */
function useFits<Box extends HTMLElement, Content extends HTMLElement>() {
  const box = useRef<Box>(null)
  const content = useRef<Content>(null)
  const [fits, setFits] = useState(true)
  useEffect(() => {
    const outer = box.current
    const inner = content.current
    if (!outer || !inner) return
    const check = () => setFits(inner.offsetWidth <= outer.clientWidth)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(outer)
    observer.observe(inner)
    return () => observer.disconnect()
  }, [])
  return { box, content, fits }
}

/**
 * The bar at the bottom: the check-up so far and a booking button. It comes
 * up with the first thing added and stays while `visible`, which the page
 * ties to the part where the check-up is put together.
 *
 * From md up the lines are chips in one row, each with its remove button.
 * When they would wrap, the row reads "Baseline plan + 3 add-ons" and opens
 * the list above it, as "Details" does on a phone, where the row holds the
 * total.
 */
export function SummaryBar({
  copy: { SUMMARY, MRI, ADDONS, PLAN },
  checkUp,
  visible,
  trackingLabel,
  trackingLocation,
  className,
}: {
  copy: Pick<Copy, "SUMMARY" | "MRI" | "ADDONS" | "PLAN">
  checkUp: CheckUp
  visible: boolean
  trackingLabel: string
  trackingLocation: string
  className?: string
}) {
  const { money } = usePlanPrices()
  const summary = useSummary(checkUp, { MRI, ADDONS })
  const [open, setOpen] = useState(false)
  const listId = useId()
  const shown = visible && (checkUp.count > 0 || checkUp.canUndo)
  const empty = checkUp.count === 0
  const row = useFits<HTMLDivElement, HTMLUListElement>()

  // An emptied selection folds the list away; its undo sits in the row. So
  // does a row that has room for its chips again.
  useEffect(() => {
    if (empty || row.fits) setOpen(false)
  }, [empty, row.fits])

  const chips = (
    <>
      {/* The plan's chip has no remove button: the plan is in every
          check-up. */}
      <li>
        <PlanChip label={PLAN.eyebrow} />
      </li>
      {summary.added.map((line) => (
        <li key={line.key} className="min-w-0">
          <LineChip line={line} SUMMARY={SUMMARY} />
        </li>
      ))}
      <li className="shrink-0">
        <ClearAllButton
          onClick={checkUp.clear}
          SUMMARY={SUMMARY}
          className="ml-1 whitespace-nowrap"
        />
      </li>
    </>
  )

  return (
    <div
      inert={!shown}
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white/95 shadow-[0_-8px_24px_rgba(0,0,0,0.06)] backdrop-blur transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
        shown ? "translate-y-0" : "translate-y-full",
        className
      )}
    >
      {/* The list opens above the row and takes the full width: the chat
          launcher only sits beside the row. From md up it opens only from
          a row too full for its chips. */}
      <div
        id={listId}
        className={cn(
          "max-h-[50vh] overflow-y-auto border-b border-neutral-100 px-4 pt-2 pb-2 min-[1340px]:pr-10 md:px-10 md:pr-28",
          !open && "hidden",
          row.fits && "md:hidden"
        )}
      >
        <div className="md:mx-auto md:max-w-[1100px]">
          <div className="md:max-w-[520px]">
            <LineList summary={summary} SUMMARY={SUMMARY} />
            <div className="flex items-center justify-between gap-4 pt-1">
              <p className="type-body-sm text-neutral-500">
                {SUMMARY.renewal(money(summary.plan))}
              </p>
              {!empty && (
                <ClearAllButton onClick={checkUp.clear} SUMMARY={SUMMARY} />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* The right padding keeps the floating chat launcher (64 px, 24 or
          32 px from the corner) off the button until the row sits far
          enough from the edge. */}
      <div className="px-4 pr-24 min-[1340px]:pr-10 md:px-10 md:pr-28">
        <div className="mx-auto max-w-[1100px] py-3 md:py-4">
          <div className="flex items-center justify-between gap-4 md:gap-8">
            <div className="hidden min-w-0 flex-1 md:block">
              <p className="type-title-md text-neutral-900">{SUMMARY.title}</p>
              <div ref={row.box} className="relative mt-1.5 overflow-hidden">
                {/* The chips at their own width, unseen, to tell whether
                    they fit in one row. */}
                <ul
                  ref={row.content}
                  aria-hidden
                  inert
                  className="invisible absolute top-0 left-0 flex w-max items-center gap-2"
                >
                  {chips}
                </ul>
                {empty ? (
                  <UndoNote checkUp={checkUp} SUMMARY={SUMMARY} />
                ) : row.fits ? (
                  <ul className="flex items-center gap-2">{chips}</ul>
                ) : (
                  <div className="flex items-center gap-2">
                    <PlanChip label={PLAN.eyebrow} />
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-controls={listId}
                      onClick={() => setOpen((o) => !o)}
                      className="type-title-sm inline-flex h-9 shrink-0 items-center gap-1 rounded-full bg-neutral-100 pr-2.5 pl-3.5 whitespace-nowrap text-neutral-900 transition-colors hover:bg-neutral-200"
                    >
                      {SUMMARY.addons(summary.added.length)}
                      <ChevronDownIcon
                        width={18}
                        height={18}
                        className={cn(
                          "transition-transform duration-200",
                          !open && "rotate-180"
                        )}
                      />
                    </button>
                    <ClearAllButton
                      onClick={checkUp.clear}
                      SUMMARY={SUMMARY}
                      className="ml-1 whitespace-nowrap"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="min-w-0 md:hidden">
              <p aria-live="polite" className="type-title-lg text-neutral-900">
                {money(summary.total)}
              </p>
              {empty ? (
                <UndoNote
                  checkUp={checkUp}
                  SUMMARY={SUMMARY}
                  className="type-body-sm"
                />
              ) : (
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={listId}
                  onClick={() => setOpen((o) => !o)}
                  className="type-body-sm text-neutral-600 underline underline-offset-4"
                >
                  {open ? SUMMARY.hideDetails : SUMMARY.details}
                </button>
              )}
            </div>
            <div className="hidden shrink-0 text-right md:block">
              <p aria-live="polite" className="type-title-lg text-neutral-900">
                {money(summary.total)}
              </p>
              <p className="type-body-sm whitespace-nowrap text-neutral-500">
                {SUMMARY.renewal(money(summary.plan))}
              </p>
            </div>
            <BookButton
              scan={summary.scan}
              addOnIds={summary.addOnIds}
              trackingLabel={trackingLabel}
              trackingLocation={trackingLocation}
              className="shrink-0"
            >
              <span className="md:hidden">{SUMMARY.ctaShort}</span>
              <span className="hidden md:inline">{SUMMARY.cta}</span>
            </BookButton>
          </div>
        </div>
      </div>
    </div>
  )
}
